// ============================================================================
// GTO CHARTS - preflop reference a player can read on a phone
// ----------------------------------------------------------------------------
// The full-page home of the preflop charts. The same chart also opens in a
// drawer from the floating Charts button on every training screen, preset
// to the seat and stack the player is in.
// ============================================================================
import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Container, HStack, VStack, Text, Heading, Button, Stack } from '@chakra-ui/react';
import { ArrowLeft, HelpCircle } from 'lucide-react';
import GtoChart, { GtoSituation } from '../components/training/GtoChart';

const GtoChartsPage: React.FC = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [showHelp, setShowHelp] = useState(false);
  const situation = (params.get('situation') as GtoSituation | null) || undefined;
  const position = params.get('position') || undefined;
  const stackBB = params.get('stack') ? Number(params.get('stack')) : undefined;

  return (
    <Box minH="100vh" bg="#0a1420" pb={24}>
      <Container maxW="container.md" pt={4} px={{ base: 3, md: 6 }}>
        <HStack justify="space-between" mb={2}>
          <Button size="sm" variant="ghost" color="gray.300" leftIcon={<ArrowLeft size={16} />} onClick={() => navigate('/training')}>Training</Button>
          <Button size="sm" variant="ghost" color="gray.300" leftIcon={<HelpCircle size={16} />} onClick={() => setShowHelp(v => !v)}>
            {showHelp ? 'Hide help' : 'How to read this'}
          </Button>
        </HStack>
        <Heading size="lg" color="white" mb={1}>Preflop charts</Heading>
        <Text color="gray.400" fontSize="md" mb={4}>
          What to do with each starting hand before the flop. This is how good players decide, and every training drill measures you against it.
        </Text>

        {showHelp && (
          <Box bg="#0f1722" border="1px solid rgba(0,212,212,0.35)" borderRadius="xl" p={4} mb={4}>
            <VStack align="stretch" spacing={2} fontSize="md" color="gray.200">
              <Text><b>Pairs</b> run down the middle. <b>Suited</b> hands (same suit) are above the middle, <b>offsuit</b> below.</Text>
              <Text>Green means raise, blue means call, orange means move all-in. Dark squares are folds.</Text>
              <Text>The button acts last, so it plays the most hands. The seats before it play fewer.</Text>
              <Text>GTO stands for game theory optimal: a strategy that cannot be exploited. These charts are a simplified version of what solvers recommend. Real play adjusts to the players at your table.</Text>
            </VStack>
          </Box>
        )}

        <GtoChart situation={situation} position={position} stackBB={stackBB} onPracticeClick={() => navigate('/training/scenario')} />

        <Stack mt={4} spacing={3} direction={{ base: 'column', md: 'row' }}>
          <Button flex={1} minH="50px" variant="outline" colorScheme="cyan" fontSize="md" whiteSpace="normal" onClick={() => navigate('/training/coach')}>Ask the coach about a hand</Button>
          <Button flex={1} minH="50px" variant="outline" colorScheme="gray" color="gray.200" fontSize="md" whiteSpace="normal" onClick={() => navigate('/training/glossary?category=positions')}>What the seats mean</Button>
        </Stack>
      </Container>
    </Box>
  );
};

export default GtoChartsPage;
